import React from "react";
import { Car, Gavel, Ship, FileText, Handshake, Search, ArrowRight, CheckCircle, MessageCircle } from "lucide-react";
import { services, companyInfo } from "../data/mockData";

const ServicesPage = () => {
  const iconMap = {
    car: Car,
    gavel: Gavel,
    ship: Ship,
    fileText: FileText,
    handshake: Handshake,
    search: Search,
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${companyInfo.whatsapp}`, "_blank");
  };

  const processSteps = [
    {
      step: "01",
      title: "Consultation",
      description: "Initial consultation to understand your specific vehicle requirements and preferences."
    },
    {
      step: "02", 
      title: "Vehicle Sourcing",
      description: "We source vehicles from trusted auctions and dealers across Japan using our extensive network."
    },
    {
      step: "03",
      title: "Inspection & Verification",
      description: "Thorough inspection and verification of vehicle condition, history, and documentation."
    },
    {
      step: "04",
      title: "Purchase & Documentation",
      description: "Secure purchase with complete documentation and export preparation."
    },
    {
      step: "05",
      title: "Logistics & Shipping",
      description: "Professional logistics management from pickup to your destination port."
    },
    {
      step: "06",
      title: "Delivery & Support",
      description: "Final delivery coordination and ongoing customer support."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-black via-gray-900 to-royal-red/20 flex items-center">
        <div className="container mx-auto">
          <div className="animate-fadeInUp">
            <h1 className="text-5xl font-bold text-white mb-4">Our Services</h1>
            <p className="text-xl text-gray-200 max-w-3xl">
              Comprehensive automotive solutions from auction bidding to global shipping and everything in between.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">What We Offer</h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              End-to-end automotive trading solutions designed to make your vehicle import/export experience seamless.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Car;
              return (
                <div key={service.id} className="glass-effect p-8 rounded-xl hover-lift group">
                  <div className="bg-royal-red p-4 rounded-lg w-16 h-16 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
                  <p className="text-gray-300 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-300">
                        <CheckCircle className="w-4 h-4 text-royal-red mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button 
                    onClick={handleWhatsAppClick}
                    className="btn-primary w-full justify-center"
                  >
                    Get Quote
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-black">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Our Process</h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              A streamlined 6-step process that ensures transparency and quality at every stage.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((process, index) => (
              <div key={index} className="glass-effect p-6 rounded-xl hover-lift">
                <div className="flex items-center mb-4">
                  <div className="bg-royal-red text-white font-bold text-lg w-10 h-10 rounded-full flex items-center justify-center mr-4">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-bold text-white">{process.title}</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Auction Partners */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Auction Partners</h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              We work with Japan's most prestigious auto auctions to source the finest vehicles.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {["YAHOO Auctions", "USS", "TAA", "JAA", "ORIX", "Honda", "Nissan", "Toyota"].map((auction, index) => (
              <div key={index} className="glass-effect p-6 rounded-xl text-center hover-lift">
                <h3 className="text-lg font-semibold text-white">{auction}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="section-padding bg-black">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeInLeft">
              <h2 className="text-4xl font-bold text-white mb-6">Quality Assurance</h2>
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                Every vehicle undergoes rigorous inspection and verification processes to ensure you receive 
                exactly what you expect - authentic, quality vehicles with complete documentation.
              </p>
              <div className="space-y-4">
                {[
                  "Pre-purchase inspection reports",
                  "Vehicle history verification", 
                  "Auction grade assessment",
                  "Documentation authentication",
                  "Quality assurance guarantee"
                ].map((item, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-royal-red mr-3" />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="animate-fadeInRight">
              <img
                src="https://images.unsplash.com/photo-1610478920626-fb94a144840b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXJzfGVufDB8fHx8MTc1NDM4MzczOXww&ixlib=rb-4.1.0&q=85"
                alt="Quality inspection"
                className="rounded-xl shadow-2xl hover-lift"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-royal-red">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-gray-100 mb-8 max-w-2xl mx-auto">
            Contact our experts today for personalized consultation and detailed service information.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleWhatsAppClick}
              className="bg-white text-royal-red hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp Consultation
            </button>
            <button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-royal-red px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center">
              Call: {companyInfo.landline}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;